import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mcpCall } from '../../lib/mcp';
import './RecordsTable.css';

export type SortDir = 'asc' | 'desc';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => ReactNode;
  numeric?: boolean;
  /** Enable click-to-sort on this column's header. */
  sortable?: boolean;
  /** Value used when sorting this column; defaults to row[key]. Return null for "no value"
   *  (nulls always sort last, regardless of direction). */
  sortAccessor?: (row: T) => string | number | null;
}

/** A dropdown filter over the already-fetched rows. `value === 'all'` is the no-op default. */
export interface FilterDef<T> {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  match: (row: T, value: string) => boolean;
}

/** Opt-in grouping into collapsible sections (like the Products catalog's MERV tiers). */
export interface GroupDef<T> {
  /** Section key for a row; rows with an equal key share a section. */
  keyOf: (row: T) => string | number;
  /** Display label for a section key. */
  label: (key: string | number) => string;
  /** Optional CSS color for the section's accent rail + dot (e.g. 'var(--color-merv-8)'). */
  accent?: (key: string | number) => string;
  /** Optional ordering of section keys; default is insertion order. */
  order?: (a: string | number, b: string | number) => number;
}

interface RecordsTableProps<T> {
  title: string;
  intro?: string;
  /** A read tool name (list/get prefix) the BFF forwards to the MCP. */
  tool: string;
  args?: Record<string, unknown>;
  /** Pull the row array out of the tool's payload, e.g. (r) => r.inventory. */
  select: (result: unknown) => T[];
  columns: Column<T>[];
  rowKey: (row: T, index: number) => string | number;
  emptyMessage: string;
  /** Optional controls rendered in the header (e.g. write-page bulk actions). */
  toolbar?: ReactNode;
  /** Opt-in: show a non-destructive Refresh button that re-pulls this tool's data.
   *  refetchOnWindowFocus is off and there's no polling, so without this the list only
   *  re-fetches on navigation. Off by default — pages enable it explicitly. */
  refreshable?: boolean;
  /** Optional trailing per-row action column (e.g. "Edit price"). */
  actions?: { header: string; render: (row: T) => ReactNode };
  /** Opt-in search box. `match` receives an already-lowercased, trimmed needle — the page
   *  decides which fields to compare (lowercase them too). All filtering is client-side over
   *  the fetched page, consistent with the Products catalog. */
  searchable?: { placeholder?: string; match: (row: T, needle: string) => boolean };
  /** Opt-in dropdown filters, applied client-side (AND across filters). */
  filters?: FilterDef<T>[];
  /** Opt-in initial sort. Columns must set `sortable` to be clickable. */
  defaultSort?: { key: string; dir: SortDir };
  /** Opt-in per-row class (e.g. dim deactivated records). */
  rowClassName?: (row: T, index: number) => string | undefined;
  /** Opt-in grouping into collapsible sections (applied after search/filter/sort). */
  group?: GroupDef<T>;
}

/**
 * A read-only table backed by one MCP read tool. Owns its own loading / error /
 * empty states so pages stay declarative. Capability (whether the tool is callable)
 * is enforced by the BFF + MCP role gate — a 403/empty surfaces here as a clear state.
 *
 * Search / filter / sort / group are opt-in and operate client-side over the fetched page (the
 * read tools cap `limit`, so this scales fine to the low hundreds of rows the console shows; a
 * true server-side sort/paginate is a separate `cabana-mcp` concern). Pages that pass none of the
 * optional props behave exactly as before.
 */
export function RecordsTable<T>({
  title,
  intro,
  tool,
  args,
  select,
  columns,
  rowKey,
  emptyMessage,
  toolbar,
  refreshable,
  actions,
  searchable,
  filters,
  defaultSort,
  rowClassName,
  group,
}: RecordsTableProps<T>) {
  const query = useQuery({
    queryKey: ['mcp', tool, args ?? {}],
    queryFn: async () => select(await mcpCall(tool, args ?? {})),
  });

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    () => Object.fromEntries((filters ?? []).map((f) => [f.key, 'all'])),
  );
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(defaultSort ?? null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const hasControls = Boolean(searchable) || (filters?.length ?? 0) > 0;
  const columnByKey = useMemo(() => {
    const m = new Map<string, Column<T>>();
    for (const c of columns) m.set(c.key, c);
    return m;
  }, [columns]);

  const rows = query.data ?? [];

  // Search → filters → sort, all client-side over the fetched page.
  const visible = useMemo(() => {
    let out = rows;
    const needle = search.trim().toLowerCase();
    if (searchable && needle) out = out.filter((r) => searchable.match(r, needle));
    for (const f of filters ?? []) {
      const v = filterValues[f.key];
      if (v && v !== 'all') out = out.filter((r) => f.match(r, v));
    }
    if (sort) {
      const col = columnByKey.get(sort.key);
      const accessor =
        col?.sortAccessor ??
        ((r: T) => (r as Record<string, unknown>)[sort.key] as string | number | null);
      const dir = sort.dir === 'asc' ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        const aNull = av == null || av === '';
        const bNull = bv == null || bv === '';
        if (aNull && bNull) return 0;
        if (aNull) return 1; // nulls last, regardless of direction
        if (bNull) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
      });
    }
    return out;
  }, [rows, search, filterValues, sort, searchable, filters, columnByKey]);

  // Group the visible rows into ordered sections (only when grouping is enabled).
  const sections = useMemo(() => {
    if (!group) return null;
    const groups = new Map<string | number, T[]>();
    for (const r of visible) {
      const k = group.keyOf(r);
      const bucket = groups.get(k);
      if (bucket) bucket.push(r);
      else groups.set(k, [r]);
    }
    let keys = [...groups.keys()];
    if (group.order) keys = keys.sort(group.order);
    return keys.map((k) => ({
      key: k,
      label: group.label(k),
      accent: group.accent?.(k) ?? 'var(--border-default)',
      rows: groups.get(k)!,
    }));
  }, [visible, group]);

  // Cycle: unsorted → asc → desc → unsorted.
  const toggleSort = (key: string) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });

  const toggleSection = (keyStr: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(keyStr)) next.delete(keyStr);
      else next.add(keyStr);
      return next;
    });

  const headerRow = (
    <tr>
      {columns.map((c) => {
        const active = sort?.key === c.key;
        const ariaSort = active
          ? sort!.dir === 'asc'
            ? 'ascending'
            : 'descending'
          : c.sortable
            ? 'none'
            : undefined;
        return (
          <th key={c.key} className={c.numeric ? 'records__th--num' : undefined} aria-sort={ariaSort}>
            {c.sortable ? (
              <button type="button" className="records__sort-btn" onClick={() => toggleSort(c.key)}>
                <span>{c.header}</span>
                <span
                  className={`records__sort-ind${active ? ' records__sort-ind--active' : ''}`}
                  aria-hidden="true"
                >
                  {active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}
                </span>
              </button>
            ) : (
              c.header
            )}
          </th>
        );
      })}
      {actions && <th className="records__th--actions">{actions.header}</th>}
    </tr>
  );

  const renderTable = (list: T[]) => (
    <div className="records__scroll">
      <table className="records__table">
        <thead>{headerRow}</thead>
        <tbody>
          {list.map((row, i) => (
            <tr key={rowKey(row, i)} className={rowClassName?.(row, i)}>
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'records__td--num' : undefined}>
                  {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
                </td>
              ))}
              {actions && <td className="records__td--actions">{actions.render(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="records">
      <header className="records__head">
        <div className="records__head-text">
          <h1 className="records__title">{title}</h1>
          {intro && <p className="records__intro">{intro}</p>}
        </div>
        {(refreshable || toolbar) && (
          <div className="records__toolbar">
            {refreshable && (
              <button
                type="button"
                className="records__refresh"
                onClick={() => void query.refetch()}
                disabled={query.isFetching}
                title="Reload this data from the source"
              >
                <span
                  className={`records__refresh-icon${query.isFetching ? ' records__refresh-icon--spinning' : ''}`}
                  aria-hidden="true"
                >
                  ↻
                </span>
                {query.isFetching ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
            {toolbar}
          </div>
        )}
      </header>

      {/* Controls render as soon as the fetch succeeds — including on an empty list — so the
          search/filter affordances don't "disappear" on pages that currently have no rows. */}
      {query.isSuccess && hasControls && (
        <div className="records__controls">
          {searchable ? (
            <input
              type="search"
              className="records__search"
              placeholder={searchable.placeholder ?? 'Search…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`Search ${title}`}
            />
          ) : (
            <span />
          )}
          {filters && filters.length > 0 && (
            <div className="records__filters">
              {filters.map((f) => (
                <label key={f.key} className="records__filter">
                  <span className="records__filter-label">{f.label}</span>
                  <select
                    className="records__select"
                    value={filterValues[f.key] ?? 'all'}
                    onChange={(e) =>
                      setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }))
                    }
                  >
                    {f.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="records__panel">
        {query.isPending && (
          <div className="records__state" role="status" aria-live="polite">
            <span className="records__spinner" aria-hidden="true" />
            Loading…
          </div>
        )}

        {query.isError && (
          <div className="records__state records__state--error" role="alert">
            <strong>Couldn’t load this data.</strong>
            <span className="records__state-detail">{(query.error as Error).message}</span>
            <button type="button" className="records__retry" onClick={() => query.refetch()}>
              Try again
            </button>
          </div>
        )}

        {query.isSuccess && rows.length === 0 && (
          <div className="records__state">{emptyMessage}</div>
        )}

        {query.isSuccess && rows.length > 0 && visible.length === 0 && (
          <div className="records__state">No records match the current search and filters.</div>
        )}

        {/* Grouped sections (like the Products catalog) when a group is provided; otherwise a
            single flat table. */}
        {query.isSuccess && visible.length > 0 && sections && (
          <div className="records__sections">
            {sections.map((s) => {
              const keyStr = String(s.key);
              const isCollapsed = collapsed.has(keyStr);
              return (
                <div className="records__section" key={keyStr}>
                  <button
                    type="button"
                    className="records__section-head"
                    style={{ borderLeftColor: s.accent }}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleSection(keyStr)}
                  >
                    <span className="records__section-chevron" aria-hidden="true">
                      {isCollapsed ? '▸' : '▾'}
                    </span>
                    <span
                      className="records__section-dot"
                      style={{ backgroundColor: s.accent }}
                      aria-hidden="true"
                    />
                    <span className="records__section-title">{s.label}</span>
                    <span className="records__section-count">{s.rows.length}</span>
                  </button>
                  {!isCollapsed && renderTable(s.rows)}
                </div>
              );
            })}
          </div>
        )}

        {query.isSuccess && visible.length > 0 && !sections && renderTable(visible)}
      </div>
    </section>
  );
}
